import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ResponsivePie } from '@nivo/pie';
import { Card, CardBody, CardTitle } from 'reactstrap';
import { loadAccountingStats } from '../../redux';
import { Utils } from '../../services';
import theme from './theme';

const AccountingPieChart = ({
    group_by,
    chartDataBuilder,
    filters = {},
    icon = 'fa-chart-pie',
    currency = 'chf',
    colorScheme = 'nivo',
    onClickOnNode
}) => {
    const HASH = `by_${group_by}`;

    const { t, i18n } = useTranslation();
    const dispatch    = useDispatch();

    const money  = amount => Utils.formatMoney(amount, i18n.language, currency);
    const number = number => Utils.formatNumber(number, i18n.language);

    const stats             = useSelector(state => state.accountingStats.stats)[HASH];
    const { KUserSettings } = useSelector(state => state.userSettings);

    useEffect(() => {
        dispatch(loadAccountingStats(HASH, { ...filters, group_by }));
    }, [dispatch, filters, HASH, group_by]);

    const pieChartData = useMemo(() => (
        stats?.data?.current?.results?.map(chartDataBuilder)
    ), [stats]);
    const totalCount = useMemo(() => (
        pieChartData?.reduce((sum, item) => sum + item.data.count, 0)
    ), [stats]);
    const totalValue = useMemo(() => (
        pieChartData?.reduce((sum, item) => sum + item.value, 0)
    ), [stats]);

    const title = t(`accounting.stats.${HASH}.title`);

    const onClick = (node, event) => {
        onClickOnNode(node.data._id);
    };

    if (stats?.isLoading)
        return (
            <Card className="mb-4">
                <CardBody>
                    <CardTitle>
                        <i className={`fa ${icon}`}></i> {title}
                    </CardTitle>
                    <div className="text-center py-5">
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-2">{t('accounting.stats.loading_data')}</p>
                    </div>
                </CardBody>
            </Card>
        );

    if (stats?.error)
        return (
            <Card className="mb-4">
                <CardBody>
                    <CardTitle>
                        <i className={`fa ${icon}`}></i> {title}
                    </CardTitle>
                    <div className="text-center py-5 text-danger">
                        <i className="fa fa-exclamation-triangle fa-2x"></i>
                        <p className="mt-2">{stats?.error}</p>
                    </div>
                </CardBody>
            </Card>
        );

    if (!pieChartData?.length)
        return (
            <Card className="mb-4">
                <CardBody>
                    <CardTitle>
                        <i className={`fa ${icon}`}></i> {title}
                    </CardTitle>
                    <div className="text-center py-5">
                        <p>{t('accounting.stats.no_data')}</p>
                    </div>
                </CardBody>
            </Card>
        );

    return (
        <Card className="mb-4">
            <CardBody>
                <CardTitle>
                    <i className={`fa ${icon}`}></i> {title}
                </CardTitle>

                <div className="mb-3">
                    <p className="mb-1"><strong>{t('accounting.stats.nb_of_transactions')}:</strong> {number(totalCount)}</p>
                    <p className="mb-1"><strong>{t('accounting.stats.total_amount')}:</strong> { money(totalValue)}</p>
                </div>

                <div style={{height: 400}}>
                    <ResponsivePie
                        onClick={onClick}
                        data={pieChartData}
                        margin={{top: 40, right: 80, bottom: 80, left: 80}}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsTextColor={KUserSettings.darkTheme ? '#FFFFFF' : '#333333' }
                        colors={{scheme: colorScheme}}
                        theme={theme[KUserSettings.darkTheme ? 'dark' : 'light']}
                        tooltip={({datum}) => (
                            <div
                                style={{
                                    background: '#FFFFFF',
                                    color: '#000000',
                                    padding: '9px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px'
                                }}
                            >
                                <div><strong>{datum.label}</strong></div>
                                <div>{t('Montant')}: {money(datum?.value)}</div>
                                <div>{t('Transactions')}: {datum?.pieChartData?.count}</div>
                                <div>{t('Pourcentage')}: {((datum.value / totalValue) * 100).toFixed(1)}%</div>
                            </div>
                        )}
                    />
                </div>
            </CardBody>
        </Card>
    );
};

export default AccountingPieChart;
